// features/reports/components/Conciliacion.tsx
// ============================================================================
// CONCILIACIÓN del mes (bloque B0, 14-oct-2026) — pestaña dentro de Reportes › Pronóstico y presupuesto.
// Para Mabel: dónde NO cuadra y por qué, con el registro de origen a un clic. No calcula nada: lo arma el
// servidor (/finances/presupuesto/conciliacion) con las mismas libs que el presupuesto, el Excel y el PDF.
// Secciones: nómina por semana y persona (calculada · registrada · pendiente), comisiones, gastos sin concepto,
// mensualidades sin periodo, adelantos para meses siguientes, alumnas/planes/clases.
// Análisis: forobeta-ops/letsmove/ANALISIS-FINANZAS-MABEL-20261014.md · diagnóstico: DIAGNOSTICO-FINANZAS-B0-20261014.md
// ============================================================================
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, ExternalLink, Info } from 'lucide-react';
import { getConciliacion, Conciliacion as Conc, ConcSemana, ConcAlumna, marcarPrecioEspecial, quitarPrecioEspecial } from '../api';
import { useToast } from '../../../context/ToastContext';
import { moneyEntero as money } from '../../finances/api';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';

const card = 'rounded-2xl border border-line bg-surface p-4 shadow-sm';
const th = 'py-2 pr-2 text-left text-xs font-medium uppercase tracking-wide text-content-3';
const thR = `${th} text-right`;
const fechaCorta = (iso: string) => { const d = new Date(iso + 'T12:00:00'); return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }); };
const dif = (n: number) => (Math.abs(n) < 0.005 ? '—' : (n > 0 ? '+' : '−') + money(Math.abs(n)));
const colorDif = (n: number) => (Math.abs(n) < 0.005 ? 'text-content-3' : n > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-sky-700 dark:text-sky-300');

function Seccion({ titulo, resumen, ok, abierta, onToggle, children }: { titulo: string; resumen: string; ok?: boolean; abierta: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div className={`${card} mt-4`}>
      <button onClick={onToggle} className="flex w-full items-center justify-between gap-2 text-left">
        <h3 className="flex items-center gap-2 font-display text-base font-bold text-content">
          {ok === true && <CheckCircle2 size={16} className="shrink-0 text-emerald-600" />}
          {ok === false && <AlertTriangle size={16} className="shrink-0 text-amber-600" />}
          {titulo}
        </h3>
        <span className="flex items-center gap-2 text-right text-xs text-content-3">{resumen} <ChevronDown size={16} className={`shrink-0 transition ${abierta ? 'rotate-180' : ''}`} /></span>
      </button>
      {abierta && <div className="mt-3">{children}</div>}
    </div>
  );
}

function FichaLink({ id, children }: { id: number | null | undefined; children: React.ReactNode }) {
  if (!id) return <>{children}</>;
  return <Link to={`/alumnos/${id}?t=pagos`} className="inline-flex items-center gap-1 text-brand-blue hover:underline">{children} <ExternalLink size={11} /></Link>;
}

function Semana({ w, futura, hoy }: { w: ConcSemana; futura: boolean; hoy: string }) {
  const [abierta, setAbierta] = useState(false);
  const enCurso = futura && w.from <= hoy;
  const estado = futura
    ? (enCurso ? 'En curso: se paga al terminar' : 'Aún no empieza')
    : Math.abs(w.pendiente) < 0.005 ? 'Cuadra' : w.pendiente > 0 ? `Falta registrar ${money(w.pendiente)}` : `Se registró ${money(-w.pendiente)} más de lo calculado`;
  const colorEstado = futura ? 'text-content-3' : Math.abs(w.pendiente) < 0.005 ? 'text-emerald-700 dark:text-emerald-300' : w.pendiente > 0 ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300';
  return (
    <>
      <tr className="border-b border-line/60">
        <td className="py-1.5 pr-2">
          <button onClick={() => setAbierta(!abierta)} className="flex items-center gap-1 text-left text-content hover:text-brand-blue">
            {abierta ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {w.label}
          </button>
        </td>
        <td className="py-1.5 pr-2 text-right tabular-nums text-content">{money(w.calculada)}</td>
        <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{futura && w.registrada === 0 ? '—' : money(w.registrada)}</td>
        <td className={`py-1.5 pr-2 text-right tabular-nums ${futura ? 'text-content-3' : colorDif(w.pendiente)}`}>{futura && w.registrada === 0 ? '—' : dif(w.pendiente)}</td>
        <td className={`py-1.5 text-xs ${colorEstado}`}>{estado}</td>
      </tr>
      {abierta && (
        <tr className="border-b border-line/60 bg-surface-2/40">
          <td colSpan={5} className="px-2 py-2">
            <table className="w-full text-xs">
              <thead><tr><th className={th}>Persona</th><th className={thR}>Horas</th><th className={thR}>Calculado</th><th className={thR}>Registrado</th><th className={thR}>Diferencia</th><th className={th}>Estado</th></tr></thead>
              <tbody>
                {w.personas.map((p) => (
                  <tr key={p.user_id} className="border-t border-line/40">
                    <td className="py-1 pr-2 text-content">{p.nombre}{p.sin_tarifa ? <span className="ml-1 text-red-600"> · sin tarifa</span> : ''}</td>
                    <td className="py-1 pr-2 text-right tabular-nums text-content-3">{p.horas ?? ''}</td>
                    <td className="py-1 pr-2 text-right tabular-nums text-content">{money(p.calculado)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums text-content-2">{p.registrado ? money(p.registrado) : '—'}</td>
                    <td className={`py-1 pr-2 text-right tabular-nums ${colorDif(p.pendiente)}`}>{dif(p.pendiente)}</td>
                    <td className="py-1 text-content-3">
                      {p.estado === 'pagada' && <span className="mr-1 rounded bg-emerald-500/10 px-1 font-semibold text-emerald-700 dark:text-emerald-300">Pagada</span>}
                      {p.estado === 'pendiente' && <span className={`mr-1 rounded px-1 font-semibold ${futura ? 'bg-surface-2 text-content-2' : 'bg-red-500/10 text-red-700 dark:text-red-300'}`}>Pendiente{p.porPagar ? ` ${money(p.porPagar)}` : ''}</span>}
                      {p.pay_mode === 'commission' ? 'Comisión: va por caja (abajo)'
                        : p.pagado ? (Math.abs(p.pendiente) < 0.005 ? 'cuadra' : p.pendiente > 0 ? 'pagado menos de lo calculado' : 'pagado más de lo calculado')
                        : futura ? 'semana en curso' : p.sin_tarifa ? 'Sin tarifa: no se puede calcular' : 'sin registrar'}
                      {p.extras > 0 ? ` · incluye ${money(p.extras)} de bonos` : ''}
                      {p.ajustes ? ` · incluye ajustes de la semana ${p.ajustes > 0 ? '+' : '−'}${money(Math.abs(p.ajustes))} (Finanzas › Nómina)` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

export default function Conciliacion({ mes }: { mes: string }) {
  const [d, setD] = useState<Conc | null>(() => readCache<Conc>(`rep-conciliacion-${mes}`) ?? null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<{ status: number; msg: string } | null>(null);
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set(['nomina']));
  const toggle = (k: string) => setAbiertas((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const [recarga, setRecarga] = useState(0);
  const [marcando, setMarcando] = useState<number | null>(null);
  const toast = useToast();

  useEffect(() => {
    let vivo = true;
    const cached = readCache<Conc>(`rep-conciliacion-${mes}`);
    setD(cached ?? null); setError(null); setCargando(!cached);
    getConciliacion(mes)
      .then((r) => { if (!vivo) return; setD(r); writeCache(`rep-conciliacion-${mes}`, r); })
      .catch((e: any) => { if (vivo) setError({ status: Number(e?.status || 0), msg: e?.message || 'No se pudo cargar la conciliación' }); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [mes, recarga]);

  // B4 (19-oct-2026): «precio especial a propósito» = deja de contar como plan ≠ clases (se borra solo si le cambian el plan).
  const precioEspecial = async (a: ConcAlumna, marcar: boolean) => {
    setMarcando(a.contact_id);
    try {
      if (marcar) await marcarPrecioEspecial(a.contact_id); else await quitarPrecioEspecial(a.contact_id);
      toast.success(marcar ? `${a.nombre}: precio especial a propósito` : `${a.nombre}: vuelve a revisarse plan vs clases`);
      setRecarga((n) => n + 1);
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setMarcando(null); }
  };

  if (error && !d) {
    return (
      <div className={`${card} mt-4 text-sm`}>
        {error.status === 404
          ? <p className="flex items-center gap-2 text-content-2"><Info size={16} className="text-brand-blue" /> La conciliación se activa cuando el servidor termine de actualizarse. Vuelve a intentar en unos minutos.</p>
          : <p className="text-red-700 dark:text-red-300">{error.msg}</p>}
      </div>
    );
  }
  if (!d) return <Skeleton className="mt-4 h-96 w-full rounded-2xl" />;

  const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Tijuana' });
  const futuras = new Set(d.nomina.semanasFuturas);
  const semanasCerradas = d.nomina.semanas.filter((w) => !futuras.has(w.from));
  const pendCerradas = semanasCerradas.reduce((s, w) => s + w.pendiente, 0);
  const nominaOk = semanasCerradas.every((w) => Math.abs(w.pendiente) < 0.005);
  const totalGastosMes = d.gastosSinConcepto.n;

  return (
    <div className={cargando ? 'opacity-60 transition' : 'transition'}>
      <p className="mb-3 text-sm text-content-3">
        <b className="text-content-2">Conciliación de {d.titulo.toLowerCase()}</b> · lo que el sistema calcula contra lo que se registró, y los datos que faltan para que todo cuadre.
      </p>

      {d.parcial && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" /> <span><b>Mes parcial.</b> {d.parcial}</span>
        </div>
      )}

      {/* KPI nómina */}
      <div className="grid grid-cols-3 gap-3">
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Nómina calculada</div>
          <div className="mt-1 font-display text-xl font-bold text-content">{money(d.nomina.totales.calculada)}</div>
          <div className="mt-0.5 text-xs text-content-3">horario × tarifa + sueldos fijos + comisión del mes</div>
        </div>
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Nómina registrada</div>
          <div className="mt-1 font-display text-xl font-bold text-content">{money(d.nomina.totales.registrada)}</div>
          <div className="mt-0.5 text-xs text-content-3">pagos registrados en Finanzas › Nómina + bonos + comisión entregada</div>
        </div>
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Pendiente de registrar</div>
          <div className={`mt-1 font-display text-xl font-bold ${d.nomina.totales.pendiente > 0.005 ? 'text-red-700 dark:text-red-300' : 'text-emerald-700 dark:text-emerald-300'}`}>{money(d.nomina.totales.pendiente)}</div>
          <div className="mt-0.5 text-xs text-content-3">{futuras.size ? `incluye ${futuras.size} semana${futuras.size === 1 ? '' : 's'} que aún no se paga${futuras.size === 1 ? '' : 'n'}` : 'calculada − registrada'}</div>
        </div>
      </div>
      {d.nomina.pendientePago && (() => {
        const pp = d.nomina.pendientePago;
        return (
          <div className={`${card} mt-3 flex flex-wrap items-center justify-between gap-2`} data-testid="conc-pendiente-pago">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-content-3">Nómina pendiente de pago</div>
              <div className="mt-0.5 text-xs text-content-3">personas-semana sin «Registrar pago» · {pp.pagadas} pagada{pp.pagadas === 1 ? '' : 's'} · {pp.pendientes} pendiente{pp.pendientes === 1 ? '' : 's'}{pp.comision > 0.005 ? ` · comisión por entregar ${money(pp.comision)}` : ''}</div>
            </div>
            <div className="text-right">
              <div className={`font-display text-xl font-bold ${pp.vencida > 0.005 ? 'text-red-700 dark:text-red-300' : 'text-content'}`}>{money(pp.total)}</div>
              <div className="text-xs text-content-3">{pp.vencida > 0.005 ? <span className="text-red-700 dark:text-red-300">{money(pp.vencida)} de semanas cerradas</span> : 'semanas cerradas al día'}{pp.enCurso > 0.005 ? ` · ${money(pp.enCurso)} en curso` : ''}</div>
            </div>
          </div>
        );
      })()}
      <p className="mt-2 flex items-start gap-1.5 text-xs text-content-3"><Info size={13} className="mt-0.5 shrink-0" /> {d.regla.texto} Este mes abarca del {fechaCorta(d.nomina.rango.from)} al {fechaCorta(d.nomina.rango.to)}.</p>

      {/* Nómina por semana */}
      <Seccion titulo="Nómina por semana y persona" ok={nominaOk} abierta={abiertas.has('nomina')} onToggle={() => toggle('nomina')}
        resumen={nominaOk ? `${semanasCerradas.length} semana${semanasCerradas.length === 1 ? '' : 's'} pagada${semanasCerradas.length === 1 ? '' : 's'}, todo cuadra` : `${semanasCerradas.filter((w) => Math.abs(w.pendiente) >= 0.005).length} semana(s) con diferencia · ${dif(pendCerradas)}`}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="border-b border-line"><th className={th}>Semana</th><th className={thR}>Calculada</th><th className={thR}>Registrada</th><th className={thR}>Diferencia</th><th className={th}>Estado</th></tr></thead>
            <tbody>{d.nomina.semanas.map((w) => <Semana key={w.from} w={w} futura={futuras.has(w.from)} hoy={hoy} />)}</tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-content-3">Calculada = sesiones del horario × tarifa (sin las «no se dio»; suplencias a quien las dio) + sueldo fijo semanal. Registrada = lo que se registró con «Registrar pago» en Finanzas › Nómina (más bonos capturados). Diferencia positiva = falta registrar; negativa = se pagó más de lo que da el horario. Pulsa una semana para ver cada persona.</p>

        {d.nomina.comisiones.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-content">Comisiones (van por el mes que corresponde la mensualidad)</h4>
            <div className="mt-1 overflow-x-auto"><table className="w-full min-w-[480px] text-sm">
              <thead><tr className="border-b border-line"><th className={th}>Persona</th><th className={thR}>Base cobrada</th><th className={thR}>Comisión del mes</th><th className={thR}>Entregada en caja</th><th className={thR}>Diferencia</th></tr></thead>
              <tbody>
                {d.nomina.comisiones.map((c) => (
                  <tr key={c.user_id} className="border-b border-line/60">
                    <td className="py-1.5 pr-2 text-content">{c.nombre}{c.pct != null ? <span className="text-xs text-content-3"> · {c.pct} %</span> : ''}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{money(c.base)}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-content">{money(c.calculada)}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{money(c.entregada)} <span className="text-xs text-content-3">({c.entregas})</span></td>
                    <td className={`py-1.5 text-right tabular-nums ${colorDif(c.pendiente)}`}>{dif(c.pendiente)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
            <p className="mt-1 text-xs text-content-3">La entrega se registra el día que sale de caja, así que en un mes puede entregarse comisión del mes anterior: una diferencia aquí no significa que falte dinero.</p>
          </div>
        )}

        <div className="mt-4">
          <h4 className="text-sm font-semibold text-content">Resumen por persona (todo el mes)</h4>
          <div className="overflow-x-auto">
            <table className="mt-1 w-full min-w-[560px] text-sm">
              <thead><tr className="border-b border-line"><th className={th}>Persona</th><th className={th}>Cómo cobra</th><th className={thR}>Horas</th><th className={thR}>Calculado</th><th className={thR}>Registrado</th><th className={thR}>Diferencia</th></tr></thead>
              <tbody>
                {d.nomina.porProfe.map((p) => (
                  <tr key={p.user_id} className="border-b border-line/60">
                    <td className="py-1.5 pr-2 text-content">{p.nombre}</td>
                    <td className={`py-1.5 pr-2 text-xs ${p.sin_tarifa ? 'text-red-600' : 'text-content-3'}`}>{p.modo}{p.sin_tarifa ? ' · sin tarifa' : ''}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-content-3">{p.horas ?? ''}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-content">{money(p.calculado)}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{money(p.registrado)}</td>
                    <td className={`py-1.5 text-right tabular-nums ${colorDif(p.diferencia)}`}>{dif(p.diferencia)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Seccion>

      {/* Gastos sin concepto */}
      <Seccion titulo="Gastos sin concepto del catálogo" ok={totalGastosMes === 0} abierta={abiertas.has('gastos')} onToggle={() => toggle('gastos')}
        resumen={totalGastosMes === 0 ? 'todos los gastos del mes tienen concepto' : `${totalGastosMes} gasto${totalGastosMes === 1 ? '' : 's'} · ${money(d.gastosSinConcepto.total)}`}>
        {totalGastosMes === 0 ? <p className="text-sm text-content-3">Todos los gastos del mes tienen Categoría › Concepto: entran completos en «Por apartado».</p> : (
          <>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-line"><th className={th}>Fecha</th><th className={th}>Categoría</th><th className={th}>Nota</th><th className={th}>Qué falta</th><th className={thR}>Importe</th></tr></thead>
              <tbody>
                {d.gastosSinConcepto.filas.map((g) => (
                  <tr key={g.expense_id} className="border-b border-line/60">
                    <td className="py-1.5 pr-2 text-content-2">{fechaCorta(g.fecha)}</td><td className="py-1.5 pr-2 text-content">{g.categoria}</td>
                    <td className="py-1.5 pr-2 text-xs text-content-3">{g.nota}</td><td className="py-1.5 pr-2 text-xs text-amber-700 dark:text-amber-300">{g.motivo}</td>
                    <td className="py-1.5 text-right tabular-nums text-content">{money(g.importe)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-content-3">Estos gastos salen como «Sin clasificar» en Por apartado. Se corrigen en <Link to="/finanzas" className="text-brand-blue hover:underline">Finanzas › Gastos</Link> eligiendo su concepto.</p>
          </>
        )}
      </Seccion>

      {/* Mensualidades sin periodo */}
      <Seccion titulo="Mensualidades sin periodo" ok={d.pagosSinPeriodo.n === 0} abierta={abiertas.has('periodo')} onToggle={() => toggle('periodo')}
        resumen={d.pagosSinPeriodo.n === 0 ? 'todas las mensualidades dicen qué mes cubren' : `${d.pagosSinPeriodo.n} cobro${d.pagosSinPeriodo.n === 1 ? '' : 's'} · ${money(d.pagosSinPeriodo.total)}`}>
        {d.pagosSinPeriodo.n === 0 ? <p className="text-sm text-content-3">Todas las mensualidades cobradas este mes indican el mes que cubren, así que el «Real devengado» (lo que corresponde al mes) se reparte bien.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-line"><th className={th}>Fecha</th><th className={th}>Alumna</th><th className={th}>Forma</th><th className={thR}>Importe</th></tr></thead>
            <tbody>
              {d.pagosSinPeriodo.filas.map((p) => (
                <tr key={p.payment_id} className="border-b border-line/60">
                  <td className="py-1.5 pr-2 text-content-2">{fechaCorta(p.fecha)}</td><td className="py-1.5 pr-2 text-content"><FichaLink id={p.contact_id}>{p.alumno}</FichaLink></td>
                  <td className="py-1.5 pr-2 text-xs text-content-3">{p.metodo}</td><td className="py-1.5 text-right tabular-nums text-content">{money(p.importe)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Seccion>

      {/* Adelantos */}
      <Seccion titulo="Cobrado por adelantado para meses siguientes" abierta={abiertas.has('adelantos')} onToggle={() => toggle('adelantos')}
        resumen={d.adelantos.n === 0 ? 'sin adelantos este mes' : `${d.adelantos.n} cobro${d.adelantos.n === 1 ? '' : 's'} · ${money(d.adelantos.total)} para otros meses`}>
        {d.adelantos.n === 0 ? <p className="text-sm text-content-3">Este mes no se cobró nada para meses siguientes.</p> : (
          <>
            <div className="flex flex-wrap gap-2 text-xs">
              {d.adelantos.porMes.map((m) => (
                <span key={m.mes} className="rounded-full bg-surface-2 px-2.5 py-1 text-content-2">{m.titulo}: <b className="text-content">{money(m.total)}</b> <span className="text-content-3">({m.n})</span></span>
              ))}
            </div>
            <p className="mt-2 text-xs text-content-3">Está en la caja de este mes ({money(d.adelantos.cobrado)} cobrados en {d.adelantos.n} pagos) pero no en el «Real devengado»: se irá sumando al mes que cubre. No es dinero que falte; es dinero que ya se cobró por clases que todavía no se dan.</p>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead><tr className="border-b border-line"><th className={th}>Fecha</th><th className={th}>Alumna</th><th className={th}>Cubre</th><th className={thR}>Cobrado</th><th className={thR}>Para otros meses</th></tr></thead>
                <tbody>
                  {d.adelantos.filas.map((a) => (
                    <tr key={a.payment_id} className="border-b border-line/60">
                      <td className="py-1.5 pr-2 text-content-2">{fechaCorta(a.fecha)}</td>
                      <td className="py-1.5 pr-2 text-content"><FichaLink id={a.contact_id}>{a.alumno}</FichaLink></td>
                      <td className="py-1.5 pr-2 text-xs text-content-3">{a.periodo}{a.meses > 1 ? ` (${a.meses} meses)` : ''}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums text-content-2">{money(a.importe)}</td>
                      <td className="py-1.5 text-right tabular-nums text-content">{money(a.parteFutura)} <span className="text-xs text-content-3">→ {a.mesesFuturos.join(', ')}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Seccion>

      {/* Alumnas, planes y clases */}
      <Seccion titulo="Alumnas, planes y clases" ok={d.alumnas.resumen.sinPlan + d.alumnas.resumen.planSinClase + d.alumnas.resumen.desajuste === 0} abierta={abiertas.has('alumnas')} onToggle={() => toggle('alumnas')}
        resumen={`${d.alumnas.resumen.activas} activas · ${d.alumnas.resumen.sinPlan} sin plan · ${d.alumnas.resumen.planSinClase} con plan sin clase · ${d.alumnas.resumen.desajuste} plan ≠ clases${d.alumnas.resumen.precioEspecial ? ` · ${d.alumnas.resumen.precioEspecial} precio especial` : ''}`}>
        <div className="grid gap-4 text-sm md:grid-cols-3">
          <div>
            <h4 className="font-semibold text-content">Activas sin plan <span className="font-normal text-content-3">({d.alumnas.sinPlan.length})</span></h4>
            <p className="mb-1 text-xs text-content-3">No entran al presupuesto (activas × plan).</p>
            {d.alumnas.sinPlan.length ? d.alumnas.sinPlan.map((a) => <div key={a.contact_id} className="border-b border-line/60 py-1"><FichaLink id={a.contact_id}>{a.nombre}</FichaLink>{a.inscritas ? <span className="text-xs text-content-3"> · en {a.inscritas} clase{a.inscritas === 1 ? '' : 's'}</span> : ''}</div>) : <p className="text-xs text-content-3">Ninguna.</p>}
          </div>
          <div>
            <h4 className="font-semibold text-content">Con plan pero sin clase vigente <span className="font-normal text-content-3">({d.alumnas.planSinClase.length})</span></h4>
            <p className="mb-1 text-xs text-content-3">Se les presupuesta su plan aunque no están inscritas en ninguna clase: ¿baja sin registrar?</p>
            {d.alumnas.planSinClase.length ? d.alumnas.planSinClase.map((a) => <div key={a.contact_id} className="border-b border-line/60 py-1"><FichaLink id={a.contact_id}>{a.nombre}</FichaLink><span className="text-xs text-content-3"> · {a.plan}{a.ultimaMensualidad ? ` · última mensualidad ${fechaCorta(a.ultimaMensualidad)}` : ' · nunca pagó mensualidad'}</span></div>) : <p className="text-xs text-content-3">Ninguna.</p>}
          </div>
          <div>
            <h4 className="font-semibold text-content">Plan que no cuadra con sus clases <span className="font-normal text-content-3">({d.alumnas.desajuste.length})</span></h4>
            <p className="mb-1 text-xs text-content-3">El plan incluye N clases y está inscrita en otro número.</p>
            {d.alumnas.desajuste.length ? d.alumnas.desajuste.map((a) => (
              <div key={a.contact_id} className="border-b border-line/60 py-1" data-testid={`conc-desajuste-${a.contact_id}`}>
                <FichaLink id={a.contact_id}>{a.nombre}</FichaLink><span className="text-xs text-content-3"> · {a.plan} ({a.esperadas}) · en {a.inscritas}: {a.clases}</span>
                {d.puedeMarcarPrecio && (
                  <button onClick={() => precioEspecial(a, true)} disabled={marcando === a.contact_id} data-testid={`conc-pe-marcar-${a.contact_id}`}
                    className="ml-1 rounded-md border border-line px-1.5 py-0.5 text-[11px] text-brand-blue hover:bg-surface-2 disabled:opacity-60" title="Es una tarifa diferente a propósito: deja de contar como descuadre">Es precio especial</button>
                )}
              </div>
            )) : <p className="text-xs text-content-3">Ninguna.</p>}
            {(d.alumnas.precioEspecial?.length ?? 0) > 0 && (
              <div className="mt-3" data-testid="conc-precio-especial">
                <h4 className="font-semibold text-content">Con precio especial <span className="font-normal text-content-3">({d.alumnas.precioEspecial!.length})</span></h4>
                <p className="mb-1 text-xs text-content-3">Plan distinto a sus clases a propósito: no cuenta como descuadre.</p>
                {d.alumnas.precioEspecial!.map((a) => (
                  <div key={a.contact_id} className="border-b border-line/60 py-1">
                    <FichaLink id={a.contact_id}>{a.nombre}</FichaLink><span className="text-xs text-content-3"> · {a.plan} ({a.esperadas}) · en {a.inscritas}{a.marcadoAqui ? (a.por ? ` · marcó ${a.por.split(' ')[0]}` : '') : ' · plan elegido en la ficha'}</span>
                    {d.puedeMarcarPrecio && a.marcadoAqui && (
                      <button onClick={() => precioEspecial(a, false)} disabled={marcando === a.contact_id} data-testid={`conc-pe-quitar-${a.contact_id}`}
                        className="ml-1 rounded-md px-1.5 py-0.5 text-[11px] text-content-3 hover:bg-surface-2 hover:text-content disabled:opacity-60">quitar</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <p className="mt-3 text-xs text-content-3">{d.alumnas.nota}</p>
        <div className="mt-3 text-sm">
          <h4 className="font-semibold text-content">Clases <span className="font-normal text-content-3">· {d.clases.sinProfesor} sin profesor (rentas y ensayos)</span></h4>
          {d.clases.sinAlumnas.length ? (
            <>
              <p className="text-xs text-content-3">Con profesor y sin alumnas activas (se paga la clase y no entra nada):</p>
              {d.clases.sinAlumnas.map((c) => <div key={c.class_id} className="border-b border-line/60 py-1 text-content">{c.name} <span className="text-xs text-content-3">· {c.profesor || 'sin profesor'}</span></div>)}
            </>
          ) : <p className="text-xs text-content-3">Todas las clases con profesor tienen alumnas activas.</p>}
          <p className="mt-1 text-xs text-content-3">{d.clases.nota}</p>
        </div>
      </Seccion>

      <div className={`${card} mt-4`}>
        <h3 className="font-display text-sm font-bold text-content">Para leer bien estos números</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-content-3">{d.avisos.map((a) => <li key={a}>{a}</li>)}</ul>
        <p className="mt-2 text-xs text-content-3">Generado el {d.generado}.</p>
      </div>
    </div>
  );
}
